import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/** The vault's small bordered icon button — preview, download, a certificate.
 *
 *  ONE definition. It was written out in full in three files, so the same control was size-8 in
 *  one place and size-7 in two others, and nothing kept the three in step: a hover tweak in one
 *  file left the others behind. Renders an anchor when given `href` (downloads, certificates)
 *  and a button otherwise, because both look identical and only the element differs.
 *
 *  Grey at rest, navy border and icon on hover — the vault's one hover treatment for a bordered
 *  control. */
export function IconAction({
  label,
  href,
  onClick,
  title,
  download,
  className,
  children,
}: {
  /** What it does, for screen readers — always required; an icon alone says nothing.
   *  Carried as aria-label on BOTH shapes: an sr-only span on one and a label on the other is
   *  two mechanisms for one job, and a test written against one misses the other. */
  label: string;
  /** Present = an anchor (a download or an external view); absent = a button. */
  href?: string;
  onClick?: () => void;
  title?: string;
  download?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const shell = cn(
    'inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-line',
    'text-muted transition-colors hover:border-navy hover:text-navy',
    className,
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener"
        title={title ?? label}
        download={download}
        aria-label={label}
        className={shell}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} title={title ?? label} aria-label={label} className={shell}>
      {children}
    </button>
  );
}
